import axios from 'axios';
import { TarotCard } from '@/context/CardsContext';

const API_URL = import.meta.env.VITE_API_URL;

export interface DailyReadingPayload {
    cards: Array<{
        id: number;
        card_name: string;
        description: any;
        visible: boolean;
        reversed: boolean;
    }>;
    question: string;
    metadata: {
        type: string;
        setup: number[];
        label: string;
        questions: Array<{
            question: string;
            description: string;
        }>;
        id: string;
        description: string;
        totalCards: number;
        count: number;
        credits: number;
    };
    dailyReadingId?: string;
}

export interface DailyReading {
    _id: string;
    interpretation?: {
        detailed_reading?: {
            final_conclusion?: string;
        };
    };
}

export class DailyReadingService {
    /**
     * Create a daily reading payload based on the selected card and language
     */
    static createPayload(card: TarotCard, language: string, dailyReadingId?: string): DailyReadingPayload {
        // Map of questions by language
        const questionMap: Record<string, string> = {
            "pt-BR": "Como sera meu dia hoje?",
            "en-US": "How will my day be today?",
            "es-ES": "¿Cómo será mi día hoy?",
            "fr-FR": "Comment sera mon jour aujourd'hui?"
        };

        // Map of labels by language
        const labelMap: Record<string, string> = {
            "pt-BR": "Leitura Diária",
            "en-US": "Daily Reading",
            "es-ES": "Lectura Diaria",
            "fr-FR": "Lecture Quotidienne"
        };

        // Map of question titles by language
        const questionTitleMap: Record<string, string> = {
            "pt-BR": "Sua Orientação Diária",
            "en-US": "Your Daily Guidance",
            "es-ES": "Tu Guía Diaria",
            "fr-FR": "Votre Orientation Quotidienne"
        };

        // Map of descriptions by language
        const descriptionMap: Record<string, string> = {
            "pt-BR": "A mensagem que o universo tem para você hoje.",
            "en-US": "The message the universe has for you today.",
            "es-ES": "El mensaje que el universo tiene para ti hoy.",
            "fr-FR": "Le message que l'univers a pour vous aujourd'hui."
        };

        // Map of metadata descriptions by language
        const metadataDescriptionMap: Record<string, string> = {
            "pt-BR": "Comece seu dia com orientação cósmica. Esta leitura de carta única oferece insights rápidos e focados para ajudá-lo a navegar pela energia e oportunidades do dia.",
            "en-US": "Start your day with cosmic guidance. This one-card reading offers quick, focused insights to help you navigate the day's energy and opportunities.",
            "es-ES": "Comience su día con orientación cósmica. Esta lectura de una sola carta ofrece información rápida y enfocada para ayudarlo a navegar la energía y las oportunidades del día.",
            "fr-FR": "Commencez votre journée avec des conseils cosmiques. Cette lecture à une carte offre des aperçus rapides et ciblés pour vous aider à naviguer dans l'énergie et les opportunités de la journée."
        };

        const payload: DailyReadingPayload = {
            cards: [{
                id: card.id,
                card_name: card.card_name || '',
                description: card.description,
                visible: false,
                reversed: card.reversed || false
            }],
            question: questionMap[language] || questionMap["pt-BR"],
            metadata: {
                type: "one-card-reading",
                setup: [0, 1, 0],
                label: labelMap[language] || labelMap["pt-BR"],
                questions: [{
                    question: questionTitleMap[language] || questionTitleMap["pt-BR"],
                    description: descriptionMap[language] || descriptionMap["pt-BR"]
                }],
                id: "daily-reading",
                description: metadataDescriptionMap[language] || metadataDescriptionMap["pt-BR"],
                totalCards: 1,
                count: 1,
                credits: 1
            }
        };

        // Include dailyReadingId if provided
        if (dailyReadingId) {
            payload.dailyReadingId = dailyReadingId;
        }

        return payload;
    }

    /**
     * Create a daily reading on the backend
     */
    static async createReading(token: string, payload: DailyReadingPayload): Promise<DailyReading> {
        const response = await axios.post(`${API_URL}/api/tarot/daily-reading`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    }
}

